/**
 * tokens.ts — Design Token SSOT (type-safe layer)
 *
 * SYMMETRIC SSOT — sync with:
 *   - miniapp/src/styles/theme.css  (CSS vars on :root, physical SSOT)
 *   - miniapp/tailwind.config.js    (Tailwind bridge via rgb(var(--token) / alpha))
 * See ADR-027 (Amendment 2026-06-05: palette reverted to prototype fidelity).
 * Hex values are written in exactly these 3 files.
 *
 * DESIGN SOURCE: ai/designs/miniapp/project/prototype.jsx (P/S/G/T). This file is a
 * faithful 1:1 port of that design system. The earlier prod divergence (#C0513A main
 * terracotta, flattened glass, weight-600 titles) was reverted — see ADR-027 amendment.
 *
 * P/S = palette/semantic — use as Tailwind classes or CSS vars where possible.
 * G   = glass recipes — consumed as React inline styles because Tailwind cannot
 *       compose `backdrop-filter + box-shadow + border` in a single utility.
 * T   = typography — font-family stack strings + shorthand font scales.
 */

// ─── Palette ─────────────────────────────────────────────────────────────────
// SYMMETRIC SSOT — sync with theme.css :root + tailwind.config.js — see ADR-027
// Mirrors prototype.jsx const P (lines 5-25).
export const P = {
  terracotta: '#E2725B',  // canonical — main brand accent (prototype.jsx)
  terraDark:  '#C0513A',  // pressed / shadow terracotta (was prod "canonical" pre-revert)
  terraLight: '#F0977F',
  terraSoft:  '#FFE1D8',
  jade:       '#356B6B',
  jadeSoft:   'rgba(53,107,107,0.10)',
  cream:      '#F7F1D9',
  card:       '#FFFFFF',
  bg:         '#F2EFE6',  // page / grouped background (prototype P.bg)
  ink:        '#2D2518',  // warm near-black (prototype P.ink)
  inkSoft:    'rgba(45,37,24,0.55)',
  line:       'rgba(60,50,35,0.10)',
  mist:       'rgba(45,37,24,0.06)',
  muted:      '#8A7F6E',
  success:    '#2D8A4E',
  warn:       '#D4890E',
  error:      '#D14343',
  danger:     '#D14343',  // alias of error (prod screens reference P.danger)
} as const;

// ─── Semantic ─────────────────────────────────────────────────────────────────
// Mirrors prototype.jsx const S (lines 31-51) + prod-only helpers (overlay*, focusRing…).
export const S = {
  label:           'rgb(45 37 24)',
  secondaryLabel:  'rgba(45,37,24,0.60)',
  tertiaryLabel:   'rgba(45,37,24,0.30)',
  quaternaryLabel: 'rgba(45,37,24,0.18)',
  separator:       'rgba(60,50,35,0.12)',
  separatorOpaque: 'rgba(60,50,35,0.18)',
  groupedBg:       '#F2EFE6',
  secondaryGroupedBg: '#FFFFFF',
  tertiaryGroupedBg:  '#F4F1E8',
  cardBg:          '#FFFFFF',
  tint:            '#E2725B',
  tintInk:         '#2D2518',
  tintTerra:       '#E2725B',
  tintJade:        '#356B6B',
  systemRed:       '#D14343',
  systemGreen:     '#2D8A4E',
  systemOrange:    '#D4890E',
  // dark-on-content (tinted overlays on dark backgrounds)
  labelDark:           '#FFFFFF',
  secondaryLabelDark:  'rgba(255,255,255,0.62)',
  tertiaryLabelDark:   'rgba(255,255,255,0.30)',
  separatorDark:       'rgba(255,255,255,0.12)',
  // prod helpers
  overlayDim:      'rgba(0,0,0,0.45)',
  overlayPress:    'rgba(45,37,24,0.06)',
  focusRing:       'rgba(226,114,91,0.35)',
  skeleton:        'rgba(45,37,24,0.08)',
} as const;

// ─── Glass Recipes ────────────────────────────────────────────────────────────
// Tailwind cannot compose backdrop-filter + box-shadow + border in one utility.
// Consume via <GlassPill variant="light"> (encapsulated) or as inline style spread.
// Browser fallback: @supports block in theme.css provides solid-color substitute.
// Mirrors prototype.jsx const G (lines 60-103) — "liquid glass": saturate 180%,
// double-inset highlight, terracotta-tinted primary affordance.
export const G = {
  light: {
    backdropFilter:       'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    background:           'rgba(247,241,217,0.55)',
    border:               '0.5px solid rgba(60,50,35,0.10)',
    boxShadow:            'inset 1.5px 1.5px 1px rgba(255,255,255,0.7), inset -1px -1px 1px rgba(255,255,255,0.4), 0 1px 3px rgba(45,37,24,0.08)',
  },
  dark: {
    backdropFilter:       'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    background:           'rgba(40,30,20,0.45)',
    border:               '0.5px solid rgba(255,255,255,0.14)',
    boxShadow:            'inset 1.5px 1.5px 1px rgba(255,255,255,0.18), inset -1px -1px 1px rgba(255,255,255,0.08), 0 2px 8px rgba(0,0,0,0.25)',
  },
  tinted: {
    backdropFilter:       'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    background:           'rgba(226,114,91,0.78)',
    border:               '0.5px solid rgba(192,81,58,0.40)',
    boxShadow:            'inset 1.5px 1.5px 1px rgba(255,255,255,0.30), 0 4px 12px rgba(192,81,58,0.22)',
  },
  warmTag: {
    backdropFilter:       'blur(16px) saturate(160%)',
    WebkitBackdropFilter: 'blur(16px) saturate(160%)',
    background:           'rgba(255,242,216,0.70)',
    border:               '0.5px solid rgba(212,137,14,0.20)',
    boxShadow:            'inset 1px 1px 0 rgba(255,255,255,0.5), 0 1px 2px rgba(45,37,24,0.06)',
  },
  successTag: {
    backdropFilter:       'blur(16px) saturate(160%)',
    WebkitBackdropFilter: 'blur(16px) saturate(160%)',
    background:           'rgba(45,138,78,0.80)',
    border:               '0.5px solid rgba(45,138,78,0.30)',
    boxShadow:            'inset 1px 1px 0 rgba(255,255,255,0.20), 0 4px 14px rgba(45,138,78,0.20)',
  },
  errorTag: {
    backdropFilter:       'blur(16px) saturate(160%)',
    WebkitBackdropFilter: 'blur(16px) saturate(160%)',
    background:           'rgba(209,67,67,0.82)',
    border:               '0.5px solid rgba(156,42,42,0.30)',
    boxShadow:            'inset 1px 1px 0 rgba(255,255,255,0.20), 0 4px 14px rgba(156,42,42,0.22)',
  },
} as const;

export type GlassVariant = keyof typeof G;

// ─── Marketplace badge ──────────────────────────────────────────────────────────
// Brand colors of EXTERNAL marketplaces (Wildberries / Ozon) — these are their own
// brand identities, NOT part of our prototype palette, so they are intentionally
// outside the ADR-027 hex SSOT chain (no theme.css / tailwind.config.js sibling).
// JS-only, mirrors miniapp/src/screens/flow/atoms/SocialIcon.tsx PLATFORM_STYLES.
//   - Ozon → #005BFF (bright blue)   - WB → #CB11AB (magenta / violet)
// Used by the top-left overlay pill on product cards (TasksScreen / FindsScreen /
// HomeScreen RecommendationsList). marketplace string is buyer-facing "WB" | "Ozon".
export const MARKETPLACE_BADGE = {
  ozon: { background: '#005BFF', boxShadow: '0 2px 6px rgba(0,91,255,0.40)' },
  wb:   { background: '#CB11AB', boxShadow: '0 2px 6px rgba(203,17,171,0.40)' },
} as const;

export function marketplaceBadgeStyle(marketplace: string): {
  background: string;
  color: string;
  boxShadow: string;
} {
  const isOzon = marketplace.toLowerCase().includes('oz');
  const brand = isOzon ? MARKETPLACE_BADGE.ozon : MARKETPLACE_BADGE.wb;
  return { background: brand.background, color: '#fff', boxShadow: brand.boxShadow };
}

// ─── Typography ───────────────────────────────────────────────────────────────
// Family stacks (also CSS vars --t-display / --t-body / --t-mono / --t-accent).
// Scale keys are CSS `font` shorthand strings (weight size/line family-var) for
// inline style `font:` — values mirror prototype.jsx const T (lines 338-354).
// NOTE: `font` shorthand cannot carry letter-spacing; apply letterSpacing separately
// where the prototype specifies it (display titles use ~-0.3, hero ~-0.8).
export const T = {
  // Family stacks
  fontDisplay: '"Bitter", Georgia, serif',
  fontBody:    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter", system-ui, sans-serif',
  fontMono:    'ui-monospace, SFMono-Regular, "JetBrains Mono", monospace',
  fontAccent:  '"Caveat", "Caveat Variable", cursive',
  // Scale shorthands — restored prototype weights/sizes (700 serif titles)
  largeTitle:  '700 34px/41px var(--t-display)',
  title1:      '700 28px/34px var(--t-display)',
  title2:      '700 22px/28px var(--t-display)',
  title3:      '600 20px/1.2 var(--t-body)',
  heroAmount:  '700 44px/1.0 var(--t-display)',
  headline:    '600 17px/1.30 var(--t-body)',
  bodyText:    '400 17px/1.45 var(--t-body)',
  callout:     '400 16px/1.40 var(--t-body)',
  subhead:     '400 15px/1.40 var(--t-body)',
  footnote:    '400 13px/1.40 var(--t-body)',
  caption:     '500 12px/1.30 var(--t-body)',
  captionAlt:  '400 12px/1.30 var(--t-body)',
  label:       '500 11px/1.20 var(--t-body)',
  tag:         '600 11px/1.20 var(--t-body)',
  // Brand-specific (caveat handwriting accents)
  caveatLg:    '700 28px/1.0 var(--t-accent)',
  caveatMd:    '700 20px/1.0 var(--t-accent)',
} as const;

// ─── Layout Constants ─────────────────────────────────────────────────────────
// LAYOUT — not a color token; consumed by IOSScreen tabBarSpace + AppShell TabBar.
// NOT part of the ADR-027 hex SSOT chain — no theme.css / tailwind.config.js sibling needed.
// SYMMETRIC: consumed by:
//   - miniapp/src/components/atoms/IOSScreen.tsx (tabBarSpace paddingBottom)
//   - miniapp/src/screens/AppShell.tsx (TabBar bottom offset + positioning)
export const TAB_BAR = {
  /** Visual height of the capsule (px) */
  height: 64,
  /** Distance from bottom of viewport to bottom of capsule (px) */
  bottomOffset: 24,
  /** Total scroll clearance = height + bottomOffset + ~16px breathing room */
  clearance: 104,
} as const;
